"use client";

import React, { useCallback, useMemo } from "react";
import { resolveCustomerPrepayment } from "@openings/booking-core";
import { ComponentProps } from "./types";
import Button from "antd/es/button";
import Flex from "antd/es/flex";
import Typography from "antd/es/typography";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import { extractErrorMessage } from "@/lib/helpers/error";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import {
  getCancellationPolicyText,
  getUpfrontRefundPolicyText,
} from "@utils/core";
import { MODAL_COMPONENT_SIZE, RESCHEDULE_MAIN } from "./consts";
import InlineError from "./components/InlineError";
import styles from "./appointmentFlow.module.css";
import { BookingSkinTokensContext } from "../../BookingPage/bookingSkinContext";
import {
  shouldApplyCustomerDepositGate,
  shouldOfferExistingAppointmentReschedule,
} from "./quoteRouting";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function formatCurrency(amount: number) {
  return currencyFormatter.format(Math.max(amount, 0));
}

// booking.payment.collectionAgent — Openings is the Stripe business of record
// and collects on the business's behalf (platform terms, "Payments").
const COLLECTION_AGENT_NOTICE =
  "Openings, Inc. collects card payments on behalf of the business. Your card statement may show “OPENINGS” followed by the business name.";

// Stripe draws the card field in its own iframe, out of reach of the mobile
// 16px input rule in `styles/globals.scss`, so match that rule's query here.
// Under 16px, iOS zooms the page when the customer focuses the card number
// (#725: the viewport no longer locks the scale).
const MOBILE_INPUT_QUERY = "screen and (max-width: 768px)";

function formatServiceNames(names: string[]) {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

const SetupPayment: React.FC<ComponentProps> = ({
  appointmentDetails,
  state,
  setState,
  onCreateAppointment,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { matches: isMobileInput } = useMediaQuery(MOBILE_INPUT_QUERY);
  const skinTokens = React.useContext(BookingSkinTokensContext);
  const cardElementOptions = useMemo<StripeCardElementOptions>(
    () => ({
      style: {
        base: {
          fontSize: isMobileInput ? "16px" : "14px",
          // The card field is an iframe no CSS variable reaches, so a skin
          // hands Stripe its ink directly (a dark skin is unreadable in
          // Stripe's default dark-on-light). Flat presets keep Stripe's own.
          ...(skinTokens
            ? {
                color: skinTokens.textColor,
                iconColor: skinTokens.textSecondaryColor,
                fontFamily: skinTokens.bodyFontFamily,
                "::placeholder": { color: skinTokens.textSecondaryColor },
              }
            : {}),
        },
      },
    }),
    [isMobileInput, skinTokens],
  );
  const {
    upcomingAppointments,
    customerId,
    customerDepositRequired,
    verificationCode,
  } = state.state;

  const policyEnabled = appointmentDetails?.cancellationPolicyEnabled !== false;
  const policyText = useMemo(
    () =>
      policyEnabled
        ? getCancellationPolicyText(
            appointmentDetails?.cancellationPolicyHours,
            appointmentDetails?.cancellationPolicyNote,
            appointmentDetails?.lateGraceMinutes,
          )
        : null,
    [
      policyEnabled,
      appointmentDetails?.cancellationPolicyHours,
      appointmentDetails?.cancellationPolicyNote,
      appointmentDetails?.lateGraceMinutes,
    ],
  );

  const paymentSummary = useMemo(() => {
    const services = appointmentDetails?.services ?? [];

    const pickNumber = (value: unknown) => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return Math.round(value * 100) / 100;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
    };

    const normalizeRequirement = (
      service: (typeof services)[number],
    ): "NONE" | "DEPOSIT" | "FULL" => {
      if (service?.paymentRequirement === "FULL") return "FULL";
      if (service?.paymentRequirement === "DEPOSIT") return "DEPOSIT";
      if (service?.hasPayAdvance) return "DEPOSIT";
      if (service?.paymentRequirement === "NONE") return "NONE";
      return "NONE";
    };

    const summary = services.reduce(
      (acc, service) => {
        const requirement = normalizeRequirement(service);
        const optionPrice = pickNumber(service?.option?.price);
        const basePrice = pickNumber(service?.price);
        const price = optionPrice > 0 ? optionPrice : basePrice;
        const depositValueRaw = service?.depositAmount;
        const depositValueCandidate = pickNumber(depositValueRaw);
        const deposit =
          depositValueCandidate > 0 ? depositValueCandidate : price;
        const serviceLabel =
          service?.option?.title || service?.title || "this service";

        if (requirement === "FULL") {
          acc.fullTotal += Math.max(price, 0);
          acc.fullNames.push(serviceLabel);
        } else if (requirement === "DEPOSIT") {
          acc.depositTotal += Math.min(Math.max(deposit, 0), Math.max(price, 0));
          acc.depositNames.push(serviceLabel);
        }

        return acc;
      },
      {
        fullTotal: 0,
        depositTotal: 0,
        fullNames: [] as string[],
        depositNames: [] as string[],
      },
    );

    const totalServicePrice = services.reduce((sum, service) => {
      const optionPrice = pickNumber(service?.option?.price);
      const basePrice = pickNumber(service?.price);
      return sum + Math.max(optionPrice > 0 ? optionPrice : basePrice, 0);
    }, 0);

    const amounts = resolveCustomerPrepayment(
      totalServicePrice,
      summary.fullTotal + summary.depositTotal,
      shouldApplyCustomerDepositGate(
        {
          requestId: appointmentDetails.requestId,
          quotePublicId: appointmentDetails.quotePublicId,
        },
        Boolean(customerDepositRequired),
      ),
    );
    if (amounts.customerPrepayment) {
      summary.fullTotal = 0;
      summary.fullNames = [];
      summary.depositTotal = amounts.chargeNow;
      summary.depositNames = [];
    }

    const type: "MIXED" | "FULL" | "DEPOSIT" | "NONE" =
      summary.fullTotal > 0 && summary.depositTotal > 0
        ? "MIXED"
        : summary.fullTotal > 0
          ? "FULL"
          : summary.depositTotal > 0
            ? "DEPOSIT"
            : "NONE";

    return { ...summary, type, totalServicePrice, ...amounts };
  }, [
    appointmentDetails.quotePublicId,
    appointmentDetails.requestId,
    appointmentDetails.services,
    customerDepositRequired,
  ]);

  const chargeNowTotal = paymentSummary.chargeNow;
  const dueLaterTotal = paymentSummary.dueLater;
  // A charge of the whole selected total is a "payment", never a "deposit"
  // (booking-flow § 6, § 11): a prepayment customer's charge, and a legacy
  // deposit with no amount that falls back to the price. The notice and the
  // refund line both read this, so they cannot disagree.
  const chargesWholeTotal = chargeNowTotal > 0 && dueLaterTotal === 0;
  // booking.payment.refundPolicy: the cancel refunds or keeps this charge by
  // the business's window (booking-flow § 9).
  const refundPolicyText =
    chargeNowTotal > 0
      ? getUpfrontRefundPolicyText({
          policyEnabled,
          policyHours: appointmentDetails?.cancellationPolicyHours,
          noun:
            paymentSummary.type === "DEPOSIT" && !chargesWholeTotal
              ? "deposit"
              : "payment",
        })
      : null;

  const primaryNotice = useMemo(() => {
    const fullNames = paymentSummary.fullNames;
    const depositNames = paymentSummary.depositNames;
    const fullLabel = fullNames.length
      ? ` for ${formatServiceNames(fullNames)}`
      : "";
    const depositLabel = depositNames.length
      ? ` for ${formatServiceNames(depositNames)}`
      : "";

    if (paymentSummary.type === "FULL" || chargesWholeTotal) {
      // A prepayment customer pays for the whole selection, so no service
      // is named.
      const prepaidNames = paymentSummary.customerPrepayment
        ? []
        : [...fullNames, ...depositNames];
      const prepaidLabel = prepaidNames.length
        ? ` for ${formatServiceNames(prepaidNames)}`
        : "";
      return `This appointment requires full prepayment of ${formatCurrency(
        chargeNowTotal,
      )}${prepaidLabel}. Your card will be charged immediately after you submit this form.`;
    }

    if (paymentSummary.type === "DEPOSIT") {
      return `A deposit of ${formatCurrency(
        paymentSummary.depositTotal,
      )}${depositLabel} is required to confirm this appointment. Your card will be charged immediately after you submit this form.`;
    }

    if (paymentSummary.type === "MIXED") {
      return `Some of your services require full prepayment and others need a deposit. We will charge ${formatCurrency(
        chargeNowTotal,
      )} immediately after you submit this form: ${formatCurrency(
        paymentSummary.fullTotal,
      )}${fullLabel} and ${formatCurrency(
        paymentSummary.depositTotal,
      )}${depositLabel}.`;
    }

    return "We require a credit card on file for this appointment.";
  }, [chargeNowTotal, chargesWholeTotal, paymentSummary]);

  const handleRouting = useCallback(
    async (paymentMethodId: string) => {
      if (!customerId) {
        setErrorMessage("We could not determine your customer record.");
        setSubmitting(false);
        return;
      }
      if (
        shouldOfferExistingAppointmentReschedule(
          {
            requestId: appointmentDetails.requestId,
            quotePublicId: appointmentDetails.quotePublicId,
          },
          upcomingAppointments.length > 0,
        )
      ) {
        setState((prev) => ({
          route: RESCHEDULE_MAIN,
          state: {
            ...prev.state,
            paymentMethodId,
          },
        }));

        setSubmitting(false);
        return;
      }

      onCreateAppointment({
        customerId,
        paymentMethodId,
        verificationCode,
        errorCallback: () => {
          setSubmitting(false);
        },
      });
    },
    [
      upcomingAppointments,
      verificationCode,
      appointmentDetails.requestId,
      appointmentDetails.quotePublicId,
      setState,
      onCreateAppointment,
      customerId,
      setSubmitting,
    ],
  );

  const handleAddCard = useCallback(async () => {
    if (!stripe || !elements) {
      setErrorMessage("Payment system unavailable. Please try again later.");
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setErrorMessage("Please enter your card details.");
      return;
    }

    setErrorMessage(null);
    setSubmitting(true);
    try {
      const res = await stripe.createPaymentMethod({ type: "card", card });
      if (res.error || !res.paymentMethod?.id) {
        throw res.error;
      }

      if (!customerId) {
        throw new Error("Customer record missing while saving payment method.");
      }

      await bookingFlowApi.createPaymentMethodForBooking({
        customerId,
        paymentId: res.paymentMethod.id,
        ...(appointmentDetails.teamMemberId
          ? { teamMemberId: appointmentDetails.teamMemberId }
          : {}),
      });

      await handleRouting(res.paymentMethod.id);
    } catch (err: any) {
      setErrorMessage(extractErrorMessage(err));
      setSubmitting(false);
    }
  }, [stripe, elements, handleRouting, customerId, appointmentDetails.teamMemberId]);
  return (
    <div className={styles.stepContainer}>
      <Flex vertical gap="middle" style={{ width: "100%" }}>
        {chargeNowTotal > 0 && (
          <div className={styles.reviewCard}>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>Due now</span>
              <Typography.Text className={styles.reviewValue}>
                {formatCurrency(chargeNowTotal)}
              </Typography.Text>
            </div>
            {dueLaterTotal > 0 && (
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Due at appointment</span>
                <Typography.Text className={styles.reviewValue}>
                  {formatCurrency(dueLaterTotal)}
                </Typography.Text>
              </div>
            )}
          </div>
        )}
        <div className={styles.stripeCard}>
          <CardElement options={cardElementOptions} />
        </div>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 13, lineHeight: 1.5 }}
        >
          {primaryNotice}
        </Typography.Text>
        {policyText && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, lineHeight: 1.5 }}
          >
            {policyText}
          </Typography.Text>
        )}
        {refundPolicyText && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, lineHeight: 1.5 }}
          >
            {refundPolicyText}
          </Typography.Text>
        )}
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, lineHeight: 1.5 }}
        >
          {COLLECTION_AGENT_NOTICE}
        </Typography.Text>
      </Flex>
      <InlineError message={errorMessage} />
      <div className={styles.actionRow}>
        <Button
          size={MODAL_COMPONENT_SIZE}
          loading={isSubmitting}
          onClick={handleAddCard}
          type="primary"
          block
        >
          {chargeNowTotal > 0
            ? `Pay ${formatCurrency(chargeNowTotal)}`
            : "Submit card"}
        </Button>
      </div>
    </div>
  );
};

export default SetupPayment; // Route constants
